/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { Logger } from '@feature-hub/core';
import type { ContentChangeListener, ContentServiceV1 } from '@oneaudi/content-service';
import type { VueFormatterFeatureServiceInterfaceV1 } from '@oneaudi/vue-formatter-service/lib/cjs/v1/vue-formatter-feature-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import React, { useState, useEffect } from 'react';
import { ContentContext } from './ContentContext';
import type { EditorJsonContent, FalconContent, TilesContent } from './contentTypes';
import { mapFaceContent, mapHeadlessContent, normalizeFalconContent } from './utils/mappers';

import { getPersonalizedTiles } from '../../__ember__personalization/getPersonalizedTiles'; // 🔥 [E]

export const ContentContextProvider: React.FC<{
  contentService: ContentServiceV1;
  vueFormatterService: VueFormatterFeatureServiceInterfaceV1;
  logger?: Logger;
  children?: React.ReactNode;
  localeService?: LocaleServiceV1;
}> = ({ contentService, vueFormatterService, logger, children, localeService }) => {
  // 🔥 [E] 👇
  const [debugTiles, setDebugTiles] = useState('normal');
  const [personalizedResults, setPersonalizedResults] = useState<unknown>(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPersonalizedResults(
        JSON.parse(window.localStorage.getItem('personalizedResults') || 'null'),
      );
    }
  }, []);
  // 🔥 [E] ☝️

  const [rawContent, setRawContent] = useState(
    contentService.getContent() as FalconContent | EditorJsonContent,
  );
  contentService.onContentChange(setRawContent as ContentChangeListener);

  // Normalize flat personalizationFeaturedTile_* fields to nested structure
  const content =
    rawContent.__type === 'aem-headless' ? normalizeFalconContent(rawContent) : rawContent;

  let tilesContent: TilesContent;
  if (content.__type === 'aem-headless') {
    logger?.info('aem-headless-content:', content);
    tilesContent = mapHeadlessContent(content);
  } else {
    tilesContent = mapFaceContent(content);
  }

  // 🔥 [E] 👇
  let isPersonalized;
  let isInContentTileMode;
  if (tilesContent.personalizationData) {
    const { isPersonalized: _isPersonalized, tiles } = getPersonalizedTiles({
      personalizedResults,
      personalizationData: tilesContent.personalizationData,
      tiles: tilesContent.tiles,
      debug: debugTiles === 'normal' ? null : debugTiles,
      featuredTile: tilesContent.personalizationFeaturedTile,
    });
    isPersonalized = _isPersonalized;
    isInContentTileMode = false;
    tilesContent.tiles = tiles;
  } else {
    isPersonalized = false;
    isInContentTileMode = true;
  }
  // 🔥 [E] ☝️

  return (
    <ContentContext.Provider
      value={{
        content: tilesContent,
        vueFormatterService,
        localeService,
        // 🔥 [E] 👇
        setPersonalizedResults,
        personalizedResults,
        isPersonalized,
        isInContentTileMode,
        debugTiles,
        setDebugTiles,
        // 🔥 [E] ☝️
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

ContentContextProvider.displayName = 'ContentContextProvider';
